import type {
  Tenant,
  Team,
  PlayerProfile,
  Event,
  ServicePlan,
  PlayerPaymentRecord,
  VideoTelemetry,
  TrainingLog,
  NotificationItem,
  TenantMember,
  ServiceSeatAllocation,
  InvoiceItem,
} from '@/types/models';

export const AVAILABLE_SERVICES: ServicePlan[] = [
  {
    id: 'BASEBALL_OPS_CORE',
    name: 'Baseball Cloud Core (チーム運用基盤)',
    description: 'マルチテナントロスター管理、打順オーダー編成、球数制限管理、多言語対応。',
    priceMonthly: 79,
    priceYearly: 790,
    allowedPaymentTypes: ['TEAM'],
    isMandatory: true,
    features: ['登録無制限ロスター', '公式打順表 (オーダーカード) 出力', '球数制限 (Pitch Smart) 準拠', '多言語UI (日英韓中)'],
    contractedSeats: 30,
  },
  {
    id: 'VIDEO_ANALYTICS',
    name: 'Video Analytics & AI Replay (4K動画解析)',
    description: '投球フォーム4Kコマ送り、打球初速・角度自動トラッキング、審判判定リプレイ解析。(チーム一括契約のみ)',
    priceMonthly: 149,
    priceYearly: 1490,
    allowedPaymentTypes: ['TEAM'],
    features: ['4Kカメラ同期・ハイスピード撮影', '打球初速 & 打ち出し角AI計測', 'TrackMan / Rapsodo データ連携', '監督・コーチ描画解説ツール'],
    contractedSeats: 15,
  },
  {
    id: 'SKILL_TRAINING',
    name: 'Skill Training & Biomechanics (動作指導)',
    description: '球速向上プログラム、守備フットワーク指導、栄養管理、個人スキルカルテ。',
    priceMonthly: 49,
    priceYearly: 490,
    allowedPaymentTypes: ['TEAM', 'INDIVIDUAL'],
    features: ['個別ブルペン投球プログラム', '週次課題・自主練ドリル動画', 'アジリティ・俊足強化モジュール', 'コーチ直接チャット指導'],
    contractedSeats: 20,
  },
  {
    id: 'PRO_RECRUITING',
    name: 'Pro Showcase & College Recruiting (進路支援)',
    description: 'NPBスカウト、大学野球連盟、米大学NCAA進路ネットワークへの公認データシート配布。',
    priceMonthly: 89,
    priceYearly: 890,
    allowedPaymentTypes: ['TEAM', 'INDIVIDUAL'],
    features: ['大学・社会人スカウト直接照会', '公認レーダー測定値レポート', '進路パイプライン管理', '4Kプロモーション動画ホスティング'],
    contractedSeats: 10,
  },
];

export const INITIAL_TENANTS: Tenant[] = [
  {
    id: '11111111-2222-3333-4444-555555555555',
    name: '東京ガス硬式野球部 (都市対抗野球)',
    slug: 'tokyo-gas-baseball',
    tier: 'PRO',
    type: 'PRO',
    seasonYear: '2025年 公式戦',
    sportType: 'baseball',
    stripeCustomerId: 'cus_tokyo_gas_9x21',
    stripeId: 'cus_tokyo_gas_9x21',
    stripeAccountId: 'cus_tokyo_gas_9x21',
  },
  {
    id: '22222222-3333-4444-5555-666666666666',
    name: '慶應義塾大学体育会野球部 (東京六大学)',
    slug: 'keio-baseball-big6',
    tier: 'COLLEGIATE',
    type: 'COLLEGIATE',
    seasonYear: '2025年 春季リーグ戦',
    sportType: 'baseball',
    stripeCustomerId: 'cus_keio_baseball_3a12',
    stripeId: 'cus_keio_baseball_3a12',
    stripeAccountId: 'cus_keio_baseball_3a12',
  },
  {
    id: '33333333-4444-5555-6666-777777777777',
    name: '武蔵府中リトルリーグ (日本リトルリーグ協会)',
    slug: 'musashi-fuchu-little',
    tier: 'ACADEMIC',
    type: 'ACADEMIC',
    seasonYear: '2025年 全日本選手権',
    sportType: 'baseball',
    stripeCustomerId: 'cus_fuchu_little_88ab',
    stripeId: 'cus_fuchu_little_88ab',
    stripeAccountId: 'cus_fuchu_little_88ab',
  },
  {
    id: '44444444-5555-6666-7777-888888888888',
    name: '世田谷西リトルシニア (中学硬式野球)',
    slug: 'setagaya-nishi-senior',
    tier: 'AMATEUR',
    type: 'AMATEUR',
    seasonYear: '2025年 日本選手権',
    sportType: 'baseball',
    stripeCustomerId: 'cus_setagaya_senior_77cd',
    stripeId: 'cus_setagaya_senior_77cd',
    stripeAccountId: 'cus_setagaya_senior_77cd',
  },
];

export const INITIAL_MEMBERS: TenantMember[] = [
  {
    id: 'mem_001',
    tenantId: '11111111-2222-3333-4444-555555555555',
    userId: 'usr_admin_kenji',
    email: 'k.takahashi@nextbase.co.jp',
    fullName: '高橋 健二 (Kenji Takahashi)',
    role: 'ADMIN',
    createdAt: '2025-01-15T08:00:00Z',
    isActive: true,
  },
  {
    id: 'mem_002',
    tenantId: '11111111-2222-3333-4444-555555555555',
    userId: 'usr_admin_seiichi',
    email: 's.suzuki@nextbase.co.jp',
    fullName: '鈴木 誠一 (Seiichi Suzuki)',
    role: 'ADMIN',
    createdAt: '2025-01-16T09:00:00Z',
    isActive: true,
  },
  {
    id: 'mem_003',
    tenantId: '11111111-2222-3333-4444-555555555555',
    userId: 'usr_coach_kudo',
    email: 'h.kudo@nextbase.co.jp',
    fullName: '工藤 裕樹 (Hiroki Kudo)',
    role: 'COACH',
    teamId: 'team_tokyo_gas_top',
    teamName: '東京ガス硬式野球部 (トップチーム)',
    createdAt: '2025-01-18T10:00:00Z',
    isActive: true,
  },
  {
    id: 'mem_004',
    tenantId: '11111111-2222-3333-4444-555555555555',
    userId: 'usr_coach_tanaka',
    email: 'k.tanaka@nextbase.co.jp',
    fullName: '田中 健太 (Kenta Tanaka)',
    role: 'COACH',
    teamId: 'team_tokyo_gas_dev',
    teamName: '東京ガス アカデミー・育成チーム',
    createdAt: '2025-01-20T11:00:00Z',
    isActive: true,
  },
  {
    id: 'mem_005',
    tenantId: '11111111-2222-3333-4444-555555555555',
    userId: 'usr_player_shota',
    email: 's.sato18@example.jp',
    fullName: '佐藤 翔太 (Shota Sato)',
    role: 'PLAYER',
    teamId: 'team_tokyo_gas_top',
    teamName: '東京ガス硬式野球部 (トップチーム)',
    createdAt: '2025-02-01T12:00:00Z',
    isActive: true,
  },
  {
    id: 'mem_006',
    tenantId: '11111111-2222-3333-4444-555555555555',
    userId: 'usr_player_yudai',
    email: 'y.yamada24@example.jp',
    fullName: '山田 雄大 (Yudai Yamada)',
    role: 'PLAYER',
    teamId: 'team_tokyo_gas_top',
    teamName: '東京ガス硬式野球部 (トップチーム)',
    createdAt: '2025-02-01T12:00:00Z',
    isActive: true,
  },
  {
    id: 'mem_007',
    tenantId: '11111111-2222-3333-4444-555555555555',
    userId: 'usr_parent_yumiko',
    email: 'yumiko.sato@example.jp',
    fullName: '佐藤 由美子 (Yumiko Sato)',
    role: 'GUARDIAN',
    createdAt: '2025-02-05T14:00:00Z',
    isActive: true,
  },
];

export const INITIAL_TEAMS: Team[] = [
  // 1. 東京ガス硬式野球部 (都市対抗野球)
  {
    id: 'team_tokyo_gas_top',
    tenantId: '11111111-2222-3333-4444-555555555555',
    name: '東京ガス硬式野球部 (トップチーム)',
    season: '2025年 公式戦',
    division: '都市対抗・社会人野球 (JABA)',
    headCoach: '工藤 裕樹 (Hiroki Kudo)',
    color: '#0284c7',
    wins: 16,
    losses: 2,
    ties: 1,
    playerCount: 22,
    stripeCustomerId: 'cus_tokyo_gas_9x21',
    paymentType: 'TEAM',
    isOwnTeam: true,
    leagueName: '都市対抗野球大会 (JABA関東地区連盟)',
    homeField: '東京ドーム / 大森グラウンド',
    contactEmail: 'h.kudo@nextbase.co.jp',
    subscriptions: [
      {
        serviceId: 'BASEBALL_OPS_CORE',
        status: 'ACTIVE',
        paymentType: 'TEAM',
        currentPeriodEnd: '2025-12-31',
        stripeSubscriptionId: 'sub_core_01',
      },
      {
        serviceId: 'VIDEO_ANALYTICS',
        status: 'ACTIVE',
        paymentType: 'TEAM',
        currentPeriodEnd: '2025-12-31',
        stripeSubscriptionId: 'sub_video_01',
      },
      {
        serviceId: 'SKILL_TRAINING',
        status: 'ACTIVE',
        paymentType: 'INDIVIDUAL',
        currentPeriodEnd: '2025-12-31',
        stripeSubscriptionId: 'sub_skill_01',
      },
    ],
  },
  {
    id: 'team_tokyo_gas_dev',
    tenantId: '11111111-2222-3333-4444-555555555555',
    name: '東京ガス アカデミー・育成チーム',
    season: '2025年 公式戦',
    division: '社会人野球 育成オープンリーグ',
    headCoach: '田中 健太 (Kenta Tanaka)',
    color: '#10b981',
    wins: 11,
    losses: 5,
    ties: 0,
    playerCount: 16,
    stripeCustomerId: 'cus_tokyo_gas_9x21',
    paymentType: 'TEAM',
    isOwnTeam: true,
    leagueName: 'JABA関東地区育成オープン戦',
    homeField: '大森グラウンド (サブグラウンド)',
    contactEmail: 'k.tanaka@nextbase.co.jp',
    subscriptions: [
      {
        serviceId: 'BASEBALL_OPS_CORE',
        status: 'ACTIVE',
        paymentType: 'TEAM',
        currentPeriodEnd: '2025-12-31',
      },
    ],
  },

  // 2. 慶應義塾大学体育会野球部 (大学野球連盟 / 東京六大学)
  {
    id: 'team_keio_big6',
    tenantId: '22222222-3333-4444-5555-666666666666',
    name: '慶應義塾大学体育会野球部 (六大学選抜)',
    season: '2025年 春季リーグ戦',
    division: '東京六大学野球連盟 1部',
    headCoach: '堀井 哲也 (Tetsuya Horii)',
    color: '#1e3a8a',
    wins: 10,
    losses: 3,
    ties: 1,
    playerCount: 28,
    stripeCustomerId: 'cus_keio_baseball_3a12',
    paymentType: 'TEAM',
    isOwnTeam: false,
    leagueName: '東京六大学野球連盟 (Tokyo Big6)',
    homeField: '明治神宮野球場 / 日吉台球場',
    contactEmail: 'baseball@keio.ac.jp',
    subscriptions: [
      {
        serviceId: 'BASEBALL_OPS_CORE',
        status: 'ACTIVE',
        paymentType: 'TEAM',
        currentPeriodEnd: '2025-12-31',
      },
      {
        serviceId: 'VIDEO_ANALYTICS',
        status: 'ACTIVE',
        paymentType: 'TEAM',
        currentPeriodEnd: '2025-12-31',
      },
    ],
  },

  // 3. 武蔵府中リトルリーグ (リトルリーグ / 学童硬式)
  {
    id: 'team_fuchu_major',
    tenantId: '33333333-4444-5555-6666-777777777777',
    name: '武蔵府中リトルリーグ (メジャー選抜 12U)',
    season: '2025年 全日本選手権',
    division: 'リトルリーグ メジャー部門 (11〜12歳)',
    headCoach: '皆川 稔 (Minoru Minagawa)',
    color: '#ea580c',
    wins: 18,
    losses: 1,
    ties: 0,
    playerCount: 15,
    stripeCustomerId: 'cus_fuchu_little_88ab',
    paymentType: 'INDIVIDUAL',
    isOwnTeam: false,
    leagueName: '日本リトルリーグ野球協会 (東京連盟)',
    homeField: '府中多摩川かぜのみち球場',
    contactEmail: 'contact@fuchu-little.jp',
    subscriptions: [
      {
        serviceId: 'BASEBALL_OPS_CORE',
        status: 'ACTIVE',
        paymentType: 'TEAM',
        currentPeriodEnd: '2025-12-31',
      },
      {
        serviceId: 'SKILL_TRAINING',
        status: 'ACTIVE',
        paymentType: 'INDIVIDUAL',
        currentPeriodEnd: '2025-12-31',
      },
    ],
  },

  // 4. 世田谷西リトルシニア (中学硬式野球 / リトルシニア)
  {
    id: 'team_setagaya_senior',
    tenantId: '44444444-5555-6666-7777-888888888888',
    name: '世田谷西リトルシニア (トップチーム 15U)',
    season: '2025年 日本選手権',
    division: 'リトルシニア 関東連盟 1部',
    headCoach: '蓬莱 昭彦 (Akihiko Horai)',
    color: '#7c3aed',
    wins: 15,
    losses: 4,
    ties: 0,
    playerCount: 20,
    stripeCustomerId: 'cus_setagaya_senior_77cd',
    paymentType: 'TEAM',
    isOwnTeam: false,
    leagueName: '日本リトルシニア中学硬式野球協会',
    homeField: '世田谷西球場',
    contactEmail: 'info@setagayanishi.jp',
    subscriptions: [
      {
        serviceId: 'BASEBALL_OPS_CORE',
        status: 'ACTIVE',
        paymentType: 'TEAM',
        currentPeriodEnd: '2025-12-31',
      },
    ],
  },
];

export const INITIAL_PLAYERS: PlayerProfile[] = [
  {
    id: 'ply_1',
    teamId: 'team_tokyo_gas_top',
    tenantId: '11111111-2222-3333-4444-555555555555',
    firstName: '翔太',
    lastName: '佐藤',
    jerseyNumber: 18,
    position: 'P',
    secondaryPosition: '1B',
    bats: 'R',
    throws: 'R',
    status: 'ACTIVE',
    email: 's.sato18@example.jp',
    phone: '090-1234-5678',
    heightInches: 73, // 185cm
    weightLbs: 185, // 84kg
    graduationYear: 2026,
    individualDuesStatus: 'PAID',
    linkedUserId: 'usr_player_shota',
    linkedUserEmail: 's.sato18@example.jp',
    linkedUserFullName: '佐藤 翔太',
    isLinkedToUser: true,
    linkStatus: 'LINKED',
  },
  {
    id: 'ply_2',
    teamId: 'team_tokyo_gas_top',
    tenantId: '11111111-2222-3333-4444-555555555555',
    firstName: '雄大',
    lastName: '山田',
    jerseyNumber: 24,
    position: 'SS',
    secondaryPosition: '2B',
    bats: 'L',
    throws: 'R',
    status: 'ACTIVE',
    email: 'y.yamada24@example.jp',
    phone: '090-2345-6789',
    heightInches: 70, // 178cm
    weightLbs: 172, // 78kg
    graduationYear: 2025,
    individualDuesStatus: 'PAID',
    linkedUserId: 'usr_player_yudai',
    linkedUserEmail: 'y.yamada24@example.jp',
    linkedUserFullName: '山田 雄大',
    isLinkedToUser: true,
    linkStatus: 'LINKED',
  },
  {
    id: 'ply_3',
    teamId: 'team_tokyo_gas_top',
    tenantId: '11111111-2222-3333-4444-555555555555',
    firstName: '拓海',
    lastName: '中村',
    jerseyNumber: 7,
    position: 'CF',
    secondaryPosition: 'LF',
    bats: 'S',
    throws: 'R',
    status: 'ACTIVE',
    email: 't.nakamura7@example.jp',
    phone: '090-3456-7890',
    heightInches: 72, // 182cm
    weightLbs: 176, // 80kg
    graduationYear: 2026,
    individualDuesStatus: 'PENDING',
    linkStatus: 'INVITED',
    isLinkedToUser: false,
    linkedUserEmail: 't.nakamura7@example.jp',
  },
  {
    id: 'ply_4',
    teamId: 'team_tokyo_gas_top',
    tenantId: '11111111-2222-3333-4444-555555555555',
    firstName: '蓮',
    lastName: '小林',
    jerseyNumber: 10,
    position: 'C',
    secondaryPosition: '3B',
    bats: 'R',
    throws: 'R',
    status: 'ACTIVE',
    email: 'r.kobayashi10@example.jp',
    phone: '090-4567-8901',
    heightInches: 71, // 180cm
    weightLbs: 190, // 86kg
    graduationYear: 2025,
    individualDuesStatus: 'PAID',
    linkStatus: 'UNLINKED',
    isLinkedToUser: false,
  },
  {
    id: 'ply_5',
    teamId: 'team_tokyo_gas_top',
    tenantId: '11111111-2222-3333-4444-555555555555',
    firstName: '隼人',
    lastName: '渡辺',
    jerseyNumber: 1,
    position: '2B',
    secondaryPosition: 'SS',
    bats: 'L',
    throws: 'R',
    status: 'ACTIVE',
    email: 'h.watanabe1@example.jp',
    phone: '090-5678-9012',
    heightInches: 69,
    weightLbs: 160,
    graduationYear: 2027,
    individualDuesStatus: 'PAID',
    linkStatus: 'UNLINKED',
    isLinkedToUser: false,
  },
  {
    id: 'ply_6',
    teamId: 'team_tokyo_gas_top',
    tenantId: '11111111-2222-3333-4444-555555555555',
    firstName: '陸',
    lastName: '伊藤',
    jerseyNumber: 5,
    position: '3B',
    secondaryPosition: '1B',
    bats: 'R',
    throws: 'R',
    status: 'ACTIVE',
    email: 'r.ito5@example.jp',
    phone: '090-6789-0123',
    heightInches: 72,
    weightLbs: 194,
    graduationYear: 2025,
    individualDuesStatus: 'PAID',
    linkStatus: 'UNLINKED',
    isLinkedToUser: false,
  },
];

export const INITIAL_SEAT_ALLOCATIONS: Record<string, ServiceSeatAllocation> = {
  VIDEO_ANALYTICS: {
    serviceId: 'VIDEO_ANALYTICS',
    serviceName: 'Video Analytics & AI Replay (4K動画解析)',
    totalSeats: 15,
    allocatedSeats: 14,
    availableSeats: 1,
    assignedMembers: [
      {
        memberId: 'mem_005',
        userId: 'usr_player_shota',
        fullName: '佐藤 翔太',
        email: 's.sato18@example.jp',
        role: 'PLAYER',
        jerseyNumber: 18,
        teamId: 'team_tokyo_gas_top',
        assignedAt: '2025-02-10T10:00:00Z',
      },
      {
        memberId: 'mem_006',
        userId: 'usr_player_yudai',
        fullName: '山田 雄大',
        email: 'y.yamada24@example.jp',
        role: 'PLAYER',
        jerseyNumber: 24,
        teamId: 'team_tokyo_gas_top',
        assignedAt: '2025-02-10T10:05:00Z',
      },
      {
        memberId: 'mem_003',
        userId: 'usr_coach_kudo',
        fullName: '工藤 裕樹 (監督)',
        email: 'h.kudo@nextbase.co.jp',
        role: 'COACH',
        teamId: 'team_tokyo_gas_top',
        assignedAt: '2025-02-01T08:00:00Z',
      },
    ],
  },
  SKILL_TRAINING: {
    serviceId: 'SKILL_TRAINING',
    serviceName: 'Skill Training & Biomechanics (動作指導)',
    totalSeats: 20,
    allocatedSeats: 12,
    availableSeats: 8,
    assignedMembers: [
      {
        memberId: 'mem_005',
        userId: 'usr_player_shota',
        fullName: '佐藤 翔太',
        email: 's.sato18@example.jp',
        role: 'PLAYER',
        jerseyNumber: 18,
        teamId: 'team_tokyo_gas_top',
        assignedAt: '2025-02-12T11:00:00Z',
      },
    ],
  },
  BASEBALL_OPS_CORE: {
    serviceId: 'BASEBALL_OPS_CORE',
    serviceName: 'Baseball Cloud Core (チーム運用基盤)',
    totalSeats: 30,
    allocatedSeats: 25,
    availableSeats: 5,
    assignedMembers: [
      {
        memberId: 'mem_001',
        userId: 'usr_admin_kenji',
        fullName: '高橋 健二 (部長)',
        email: 'k.takahashi@nextbase.co.jp',
        role: 'ADMIN',
        assignedAt: '2025-01-15T08:00:00Z',
      },
      {
        memberId: 'mem_003',
        userId: 'usr_coach_kudo',
        fullName: '工藤 裕樹 (監督)',
        email: 'h.kudo@nextbase.co.jp',
        role: 'COACH',
        teamId: 'team_tokyo_gas_top',
        assignedAt: '2025-01-18T10:00:00Z',
      },
      {
        memberId: 'mem_005',
        userId: 'usr_player_shota',
        fullName: '佐藤 翔太',
        email: 's.sato18@example.jp',
        role: 'PLAYER',
        teamId: 'team_tokyo_gas_top',
        assignedAt: '2025-02-01T12:00:00Z',
      },
    ],
  },
};

export const INITIAL_INVOICES: InvoiceItem[] = [
  {
    id: 'inv_tenant_001',
    invoiceNumber: 'INV-2025-001',
    amount: 228.0,
    currency: 'USD',
    status: 'PAID',
    payerScope: 'TEAM',
    payerName: '東京ガス硬式野球部 (チーム一括)',
    serviceName: 'Core Operations + Video Analytics (チーム一括)',
    pdfUrl: 'https://invoice.stripe.com/test_pdf_inv_001',
    date: '2025-02-01',
    periodStart: '2025-02-01',
    periodEnd: '2025-03-01',
  },
  {
    id: 'inv_tenant_002',
    invoiceNumber: 'INV-2025-002',
    amount: 228.0,
    currency: 'USD',
    status: 'PAID',
    payerScope: 'TEAM',
    payerName: '東京ガス硬式野球部 (チーム一括)',
    serviceName: 'Core Operations + Video Analytics (チーム一括)',
    pdfUrl: 'https://invoice.stripe.com/test_pdf_inv_002',
    date: '2025-03-01',
    periodStart: '2025-03-01',
    periodEnd: '2025-04-01',
  },
  {
    id: 'inv_ind_001',
    invoiceNumber: 'INV-IND-2025-101',
    amount: 49.0,
    currency: 'USD',
    status: 'PAID',
    payerScope: 'INDIVIDUAL',
    payerName: '佐藤 翔太 (投手 #18)',
    serviceName: 'Skill Training & Biomechanics (個別指導)',
    pdfUrl: 'https://invoice.stripe.com/test_pdf_inv_ind_101',
    date: '2025-02-15',
    periodStart: '2025-02-15',
    periodEnd: '2025-03-15',
  },
  {
    id: 'inv_ind_002',
    invoiceNumber: 'INV-IND-2025-102',
    amount: 49.0,
    currency: 'USD',
    status: 'PAID',
    payerScope: 'INDIVIDUAL',
    payerName: '山田 雄大 (遊撃手 #24)',
    serviceName: 'Skill Training & Biomechanics (個別指導)',
    pdfUrl: 'https://invoice.stripe.com/test_pdf_inv_ind_102',
    date: '2025-02-20',
    periodStart: '2025-02-20',
    periodEnd: '2025-03-20',
  },
];

export const INITIAL_PLAYER_PAYMENTS: PlayerPaymentRecord[] = [
  {
    id: 'pay_001',
    playerId: 'ply_1',
    serviceId: 'SKILL_TRAINING',
    amount: 49,
    currency: 'USD',
    status: 'PAID',
    date: '2025-02-15',
    paymentMethod: 'Visa •••• 4242',
    stripeInvoiceUrl: 'https://invoice.stripe.com/test_pdf_inv_ind_101',
  },
  {
    id: 'pay_002',
    playerId: 'ply_2',
    serviceId: 'SKILL_TRAINING',
    amount: 49,
    currency: 'USD',
    status: 'PAID',
    date: '2025-02-20',
    paymentMethod: 'Mastercard •••• 8899',
    stripeInvoiceUrl: 'https://invoice.stripe.com/test_pdf_inv_ind_102',
  },
];

export const INITIAL_EVENTS: Event[] = [
  {
    id: 'evt_game_1',
    tenantId: '11111111-2222-3333-4444-555555555555',
    teamId: 'team_tokyo_gas_top',
    eventType: 'GAME',
    title: '都市対抗野球大会 関東代表決定戦 第1戦',
    startTime: new Date(Date.now() + 86400000 * 1.5).toISOString(),
    endTime: new Date(Date.now() + 86400000 * 1.5 + 10800000).toISOString(),
    location: '東京ドーム (後楽園)',
    opponent: 'ENEOS野球部',
    homeAway: 'HOME',
    uniformColor: 'ホーム白・紺パイピング (白地)',
    notes: '試合前シートノックは試合開始90分前 (15:00) より開始。東京ドーム1塁側ベンチ。',
    attendanceSummary: {
      attending: 20,
      declined: 1,
      maybe: 1,
      pending: 0,
    },
  },
  {
    id: 'evt_game_2',
    tenantId: '11111111-2222-3333-4444-555555555555',
    teamId: 'team_tokyo_gas_top',
    eventType: 'GAME',
    title: 'JABA関東地区 春季オープン戦',
    startTime: new Date(Date.now() + 86400000 * 4).toISOString(),
    endTime: new Date(Date.now() + 86400000 * 4 + 10800000).toISOString(),
    location: '大森グラウンド (大田区)',
    opponent: '日本通運硬式野球部',
    homeAway: 'AWAY',
    uniformColor: 'ビジター紺ユニフォーム',
    notes: '先発予定: 佐藤 翔太 (#18)',
    attendanceSummary: {
      attending: 18,
      declined: 2,
      maybe: 2,
      pending: 0,
    },
  },
];

export const INITIAL_VIDEOS: VideoTelemetry[] = [
  {
    id: 'clip_1',
    title: 'ストレート 152km/h アウトロー見逃し三振 (佐藤 翔太 #18)',
    velo: '152 km/h (94.4 MPH)',
    spin: '2,420 RPM',
    vBreak: '+48.2 cm',
    hBreak: '-15.4 cm',
    result: '見逃し三振 (Strike 3)',
    thumbnail: 'https://images.unsplash.com/photo-1508344928928-7165b67de128?w=360&auto=format&fit=crop&q=80',
  },
];

export const INITIAL_TRAINING_LOGS: TrainingLog[] = [
  {
    id: 'trn_1',
    playerId: 'ply_1',
    date: '2025-03-04',
    category: 'PITCHING',
    durationMinutes: 60,
    notes: '傾斜を使った重心移動ドリル & スライダーリリース位置固定ブルペン投球。',
    metrics: { exitVeloMax: 94.4, launchAngleAvg: 152.0 },
  },
];

export const INITIAL_NOTIFICATIONS: NotificationItem[] = [
  {
    id: 'notif_1',
    tenantId: '11111111-2222-3333-4444-555555555555',
    title: '先発メンバー・スタメン発表',
    message: '工藤監督が都市対抗第1代表決定戦の公式先発ラインアップを公示しました。',
    time: '10分前',
    read: false,
    type: 'lineup',
    route: '/dashboard/lineup',
    createdAt: new Date(Date.now() - 10 * 60 * 1000).toISOString(),
  },
];
